import { focusEventNames, ruleSchema } from '@tunasong/schemas'
import type { Condition, EntityType, Rule } from '@tunasong/schemas'
import { isSingularKind } from './rule-vocabulary.js'

type ProfileConfig = Record<string, unknown> | undefined

/** The single Inbox rule this slice composes (Joni's spec §Scope). */
export const INBOX_CONVERT_RULE_ID = 'inbox-convert-song'
export const CONVERT_ENTITY_TO_SONG_COMMAND_ID = 'convert-entity-to-song'

/**
 * Split stored entries into the rules this client understands and the ones it
 * does not.
 *
 * Parsing the array as a whole (`automationConfigSchema.safeParse`) fails
 * entirely on a single bad entry, which read back as "no rules" and let the
 * writers below persist that emptiness — one unrecognised rule would wipe all
 * of them. Entry-by-entry keeps a bad rule's blast radius to itself, and
 * `unreadable` is carried through writes so a rule written by a newer client is
 * never destroyed by an older one.
 */
const partitionRules = (config: ProfileConfig): { rules: Rule[]; unreadable: unknown[] } => {
  const stored = (config as { rules?: unknown } | undefined)?.rules
  const rules: Rule[] = []
  const unreadable: unknown[] = []

  for (const entry of Array.isArray(stored) ? stored : []) {
    const parsed = ruleSchema.safeParse(entry)
    if (parsed.success) {
      rules.push(parsed.data)
      continue
    }
    unreadable.push(entry)
  }

  return { rules, unreadable }
}

export const readRules = (config: ProfileConfig): Rule[] => partitionRules(config).rules

const isFocusEvent = (event: string): boolean => (focusEventNames as readonly string[]).includes(event)

/** A focus rule watches the entity itself entering/leaving focus (`entered`/`left`). */
export const isFocusRule = (rule: Rule): boolean => isFocusEvent(rule.event)

/** An Inbox rule watches lifecycle events on things *inside* a host (descendants scope). */
export const isInboxRule = (rule: Rule): boolean => rule.scope === 'descendants' && !isFocusEvent(rule.event)

/** The pre-composed "tag a note `song` → convert to Song" rule, disabled for review (spec step 3). */
export const starterInboxRule = (tag = 'song'): Rule => ({
  id: INBOX_CONVERT_RULE_ID,
  event: 'tagged',
  scope: 'descendants',
  enabled: false,
  conditions: [
    { kind: 'type', entityType: 'note' },
    { kind: 'tag', tag },
  ],
  actions: [{ commandId: CONVERT_ENTITY_TO_SONG_COMMAND_ID, args: {} }],
})

const writeRules = (config: ProfileConfig, rules: Rule[]): Record<string, unknown> => ({
  ...config,
  rules: [...rules, ...partitionRules(config).unreadable],
})

export const upsertRule = (config: ProfileConfig, rule: Rule): Record<string, unknown> => {
  const rules = readRules(config)
  const next = rules.some(existing => existing.id === rule.id)
    ? rules.map(existing => (existing.id === rule.id ? rule : existing))
    : [...rules, rule]
  return writeRules(config, next)
}

export const patchRule = (config: ProfileConfig, ruleId: string, patch: Partial<Rule>): Record<string, unknown> =>
  writeRules(
    config,
    readRules(config).map(rule => (rule.id === ruleId ? { ...rule, ...patch } : rule))
  )

export const removeRule = (config: ProfileConfig, ruleId: string): Record<string, unknown> =>
  writeRules(
    config,
    readRules(config).filter(rule => rule.id !== ruleId)
  )

export const setRuleEnabled = (config: ProfileConfig, ruleId: string, enabled: boolean): Record<string, unknown> =>
  patchRule(config, ruleId, { enabled })

/**
 * Rewrites the `tag` condition at `index`, leaving every other condition —
 * including other `tag` conditions — untouched. `tag` is a repeatable
 * condition kind (AND-ing tags, e.g. `song` and `chorus`, is a supported
 * rule), so a rule can hold more than one; matching on kind alone would
 * overwrite all of them with a single edit.
 */
export const setRuleTag = (config: ProfileConfig, ruleId: string, index: number, tag: string): Record<string, unknown> =>
  writeRules(
    config,
    readRules(config).map(rule =>
      rule.id === ruleId
        ? {
            ...rule,
            conditions: rule.conditions.map((condition, at) =>
              at === index && condition.kind === 'tag' ? { ...condition, tag } : condition
            ),
          }
        : rule
    )
  )

/** Clear the loop-guard pause and turn the rule back on (spec §3 "Turn back on"). */
export const resumeRule = (config: ProfileConfig, ruleId: string): Record<string, unknown> =>
  patchRule(config, ruleId, { enabled: true, pausedReason: undefined })

/**
 * A fresh rule for the builder. Takes its id rather than minting one so tests
 * stay deterministic and this package needs no id generator.
 *
 * Always `descendants`: on a profile, `self` would mean events on the profile
 * entity itself, which is not something a user wants to automate.
 */
export const createRule = (id: string): Rule => ({
  id,
  event: 'tagged',
  scope: 'descendants',
  enabled: false,
  conditions: [{ kind: 'type', entityType: 'note' }],
  actions: [{ commandId: CONVERT_ENTITY_TO_SONG_COMMAND_ID, args: {} }],
})

const mapRule = (config: ProfileConfig, ruleId: string, change: (rule: Rule) => Rule): Record<string, unknown> =>
  writeRules(
    config,
    readRules(config).map(rule => (rule.id === ruleId ? change(rule) : rule))
  )

export const setRuleEvent = (config: ProfileConfig, ruleId: string, event: Rule['event']): Record<string, unknown> =>
  patchRule(config, ruleId, { event })

/** Replaces the type condition. Two AND-ed types match nothing, so there is only ever one. */
export const setRuleType = (config: ProfileConfig, ruleId: string, entityType: EntityType): Record<string, unknown> =>
  mapRule(config, ruleId, rule => ({
    ...rule,
    conditions: [
      { kind: 'type', entityType },
      ...rule.conditions.filter(condition => condition.kind !== 'type'),
    ],
  }))

/** Singular kinds replace; repeatable kinds append. */
export const addCondition = (config: ProfileConfig, ruleId: string, condition: Condition): Record<string, unknown> =>
  mapRule(config, ruleId, rule => ({
    ...rule,
    conditions: isSingularKind(condition.kind)
      ? [...rule.conditions.filter(existing => existing.kind !== condition.kind), condition]
      : [...rule.conditions, condition],
  }))

export const removeCondition = (config: ProfileConfig, ruleId: string, index: number): Record<string, unknown> =>
  mapRule(config, ruleId, rule => ({
    ...rule,
    conditions: rule.conditions.filter((_condition, at) => at !== index),
  }))

/**
 * Sets the action and clears its args. The previous action's arguments meant
 * something else entirely — a move destination is not an agent id — so carrying
 * them over would produce a rule that looks configured and is not.
 */
export const setRuleAction = (config: ProfileConfig, ruleId: string, commandId: string): Record<string, unknown> =>
  patchRule(config, ruleId, { actions: [{ commandId, args: {} }] })

export const setActionArg = (
  config: ProfileConfig,
  ruleId: string,
  name: string,
  value: unknown
): Record<string, unknown> =>
  mapRule(config, ruleId, rule => ({
    ...rule,
    actions: rule.actions.map((action, index) =>
      index === 0 ? { ...action, args: { ...action.args, [name]: value } } : action
    ),
  }))
